import React, { useState, useEffect, useRef } from 'react';
import { logActivity } from './utils/activityTracker';
import Sidebar from './components/Sidebar';
import ConnectedCheckoutHost from './components/checkout/ConnectedCheckoutHost';
import CustomerPortal from './components/CustomerPortal';
import DesktopHeader from './components/layout/DesktopHeader';
import MobileHeader from './components/layout/MobileHeader';
import ConnectionLoader from './components/layout/ConnectionLoader';
import AppViewRenderer from './components/layout/AppViewRenderer';
import FloatingActionButton from './components/FloatingActionButton';
import Login from './components/Login';
import { CurrencyProvider } from './components/CurrencyContext';
import { NotificationProvider } from './components/NotificationContext';
import { api } from './services/api';
import { trackPageView } from './services/analyticsService';
import {
  getParams,
  getPortalCustomerId,
  isPortalPath,
  isCustomerSession,
  hasPortalLoginToken,
  hasCheckoutIntent,
} from './utils/navigationHelper';

const AppContent: React.FC = () => {
  const processedMagicTokenRef = useRef<string | null>(null);

  const [activeView, setActiveView] = useState(() => {
    const hasPortalToken = hasPortalLoginToken();
    const storedCustomerId = localStorage.getItem('portal_customer_id');

    if (hasPortalToken || (isPortalPath() && (isCustomerSession() || storedCustomerId))) {
      return 'customer_portal';
    }
    if (hasCheckoutIntent()) return 'hosted_checkout';
    return 'dashboard';
  });

  const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    if (isCustomerSession() && !isPortalPath() && !hasPortalLoginToken()) return false;
    return api.auth.isAuthenticated();
  });
  const [isConnecting, setIsConnecting] = useState(() => hasPortalLoginToken());
  const [portalCustomerId, setPortalCustomerId] = useState<string | null>(() => {
    if (!isPortalPath() && !hasPortalLoginToken()) return null;
    return getPortalCustomerId() || localStorage.getItem('portal_customer_id');
  });

  useEffect(() => {
    (window as any).setActiveView = (view: string) => {
      if (isCustomerSession() && view !== 'customer_portal' && view !== 'hosted_checkout') return;
      setActiveView(view);
    };

    const handleAuthChange = () => {
      setIsAuthenticated(api.auth.isAuthenticated());
    };

    window.addEventListener('subscale_auth_change', handleAuthChange);
    return () => {
      delete (window as any).setActiveView;
      window.removeEventListener('subscale_auth_change', handleAuthChange);
    };
  }, []);

  useEffect(() => {
    trackPageView(activeView, `/${activeView}`);
  }, [activeView]);

  useEffect(() => {
    if (!isPortalPath() && !hasPortalLoginToken() && isCustomerSession()) {
      localStorage.removeItem('portal_customer_id');
      localStorage.removeItem('is_customer_session');
      localStorage.removeItem('subscale_auth_token');
      setPortalCustomerId(null);
      setIsAuthenticated(false);
      setActiveView('dashboard');
    }

    const params = getParams();
    const loginToken =
      params.get('login_token') ||
      params.get('magic_token') ||
      params.get('token') ||
      params.get('portal_token');
    const customerId = getPortalCustomerId();

    if (loginToken) {
      if (processedMagicTokenRef.current === loginToken) return;
      processedMagicTokenRef.current = loginToken;

      const performLogin = async () => {
        setIsConnecting(true);
        try {
          const user = await api.auth.verifyLoginToken(loginToken, customerId || '');
          const resolvedCustomerId = user.customerId || customerId;
          if (!resolvedCustomerId) throw new Error('Customer id missing from login token');

          localStorage.setItem('portal_customer_id', resolvedCustomerId);
          setPortalCustomerId(resolvedCustomerId);
          localStorage.setItem('is_customer_session', 'true');
          setIsAuthenticated(true);
          setActiveView('customer_portal');
          logActivity(resolvedCustomerId, 'login', { method: 'magic_token' });
        } catch (err) {
          console.error('Magic link verification failed', err);
          localStorage.removeItem('portal_customer_id');
          localStorage.removeItem('is_customer_session');
          localStorage.removeItem('subscale_auth_token');
          setPortalCustomerId(null);
          setIsAuthenticated(false);
          setActiveView('dashboard');
        } finally {
          setIsConnecting(false);
          window.history.replaceState({}, document.title, window.location.pathname);
        }
      };
      performLogin();
    }

    if (hasCheckoutIntent()) setActiveView('hosted_checkout');
  }, []);

  const handleLogout = async () => {
    await api.auth.logout();
    localStorage.removeItem('portal_customer_id');
    setPortalCustomerId(null);
    localStorage.removeItem('is_customer_session');
    setActiveView('dashboard');
    setIsAuthenticated(false);
  };

  if (isConnecting) return <ConnectionLoader />;
  if (activeView === 'hosted_checkout') return <ConnectedCheckoutHost onClose={() => setActiveView('dashboard')} />;
  if (activeView === 'customer_portal') return <div className="min-h-screen bg-slate-50"><CustomerPortal customerId={portalCustomerId || undefined} /></div>;
  if (!isAuthenticated) return <Login onLogin={() => setIsAuthenticated(true)} />;

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors duration-200">
      <Sidebar activeView={activeView} setActiveView={setActiveView} onLogout={handleLogout} />
      <main className="flex-1 overflow-auto">
        <MobileHeader />
        <DesktopHeader />
        <div className="p-4 md:p-6 max-w-7xl mx-auto">
          <AppViewRenderer
            activeView={activeView}
            setActiveView={setActiveView}
            selectedCustomerId={selectedCustomerId}
            setSelectedCustomerId={setSelectedCustomerId}
            selectedSubscriptionId={selectedSubscriptionId}
            setSelectedSubscriptionId={setSelectedSubscriptionId}
          />
        </div>
        <footer className="py-6 px-8 text-center text-slate-400 dark:text-slate-500 text-xs border-t border-slate-200/50 dark:border-slate-800">
          &copy; {new Date().getFullYear()} SubScale Billing Operations Engine
        </footer>
      </main>
      <FloatingActionButton hasActions={true} />
    </div>
  );
};

const App: React.FC = () => (
  <CurrencyProvider>
    <NotificationProvider>
      <AppContent />
    </NotificationProvider>
  </CurrencyProvider>
);

export default App;
